import api from './api';
import {
  ApiResponse,
  Make,
  Model,
  Color,
  Body,
  VehicleType,
  Flag,
  Dataset,
  Vehicle,
  MakeWithModels,
  FlatModel,
} from '../types';

// Search payload type
export interface MMCSearchPayload {
  make?: string;
  model?: string;
  colors?: string[];
  body?: string;
  type?: string;
  datasets?: string[];
  year?: number;
  VIN?: string;
  tag?: string;
  night?: boolean;
  flags?: {
    mustHave: string[];
    atLeastOne: string[];
    dontHave: string[];
  };
  sorting?: 'normal' | 'reverse' | 'random';
  pageSize?: number;
  page?: number;
}

// Search response type
export interface MMCSearchResponse {
  vehicles: Vehicle[];
  items?: Vehicle[];
  totalResults: number;
  page: number;
  pageSize: number;
}

// Patch payload type (uses {id} wrapper for reference fields)
export interface MMCPatchPayload {
  model?: { id: string };
  colors?: { id: string }[];
  body?: { id: string };
  type?: { id: string };
  year?: number | null;
  night?: boolean;
  flags?: { id: string }[];
  dataset?: { id: string };
  box?: number[] | null;
  vin?: string | null;
}

// Dropdown data response type
export interface MMCDropdownData {
  makes: Make[];
  models: Model[];
  colors: Color[];
  bodies: Body[];
  types: VehicleType[];
  flags: Flag[];
  datasets: Dataset[];
  makeWithModels: MakeWithModels[];
}

/**
 * Fetches all dropdown data for MMC search/edit forms.
 */
export async function fetchMMCDropdownData(signal?: AbortSignal): Promise<MMCDropdownData> {
  const config = signal ? { signal } : {};

  const [makesRes, modelsRes, colorsRes, bodiesRes, typesRes, flagsRes, datasetsRes] = await Promise.all([
    api.get<ApiResponse<Make[]>>('mmc/list/makes', config),
    api.get<ApiResponse<Model[]>>('mmc/list/models', config),
    api.get<ApiResponse<Color[]>>('mmc/list/colors', config),
    api.get<ApiResponse<Body[]>>('mmc/list/bodies', config),
    api.get<ApiResponse<VehicleType[]>>('mmc/list/types', config),
    api.get<ApiResponse<Flag[]>>('mmc/list/flags', config),
    api.get<ApiResponse<Dataset[]>>('datasets/list/mmc', config),
  ]);

  const makes = (makesRes.data.results || []) as Make[];
  const models = (modelsRes.data.results || []) as Model[];
  const colors = (colorsRes.data.results || []) as Color[];
  const bodies = (bodiesRes.data.results || []) as Body[];
  const types = (typesRes.data.results || []) as VehicleType[];
  const flags = (flagsRes.data.results || []) as Flag[];
  const datasets = (datasetsRes.data.results || []) as Dataset[];

  // Build makeWithModels by grouping models by their serie.make.id
  const makeModelsMap = new Map<string, FlatModel[]>();
  models.forEach((model: Model) => {
    const makeId = model.serie?.make?.id;
    if (!makeId) return;
    if (!makeModelsMap.has(makeId)) {
      makeModelsMap.set(makeId, []);
    }
    makeModelsMap.get(makeId)!.push({
      id: model.id,
      label: model.label,
      makeId: makeId,
    });
  });

  const makeWithModels: MakeWithModels[] = makes.map((make: Make) => ({
    id: make.id,
    label: make.label,
    models: (makeModelsMap.get(make.id) || []).sort((a, b) => a.label.localeCompare(b.label)),
  }));

  return {
    makes,
    models,
    colors,
    bodies,
    types,
    flags,
    datasets,
    makeWithModels,
  };
}

/**
 * Searches for vehicles using the given payload.
 */
export async function searchVehicles(payload: MMCSearchPayload, signal?: AbortSignal): Promise<MMCSearchResponse> {
  const config = signal ? { signal } : {};
  const response = await api.post<ApiResponse<MMCSearchResponse>>('mmc/search', payload, config);
  const data = response.data.results;

  return {
    vehicles: data.items || [],
    totalResults: data.totalResults || 0,
    page: data.page || 1,
    pageSize: data.pageSize || 20,
  };
}

/**
 * Updates a vehicle with the given patch data.
 */
export async function patchVehicle(vehicleId: string, patch: MMCPatchPayload): Promise<void> {
  await api.patch(`mmc/patch/${vehicleId}`, patch);
}

/**
 * Updates multiple vehicles with the same patch data.
 * Uses Promise.allSettled for parallel requests.
 */
export async function patchVehicles(
  vehicleIds: string[],
  patch: MMCPatchPayload
): Promise<{ succeeded: string[]; failed: string[] }> {
  const results = await Promise.allSettled(
    vehicleIds.map((id) => api.patch(`mmc/patch/${id}`, patch))
  );

  const succeeded: string[] = [];
  const failed: string[] = [];

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      succeeded.push(vehicleIds[index]);
    } else {
      failed.push(vehicleIds[index]);
    }
  });

  return { succeeded, failed };
}

/**
 * Deletes a single vehicle.
 * Admin only.
 */
export async function deleteVehicle(vehicleId: string): Promise<void> {
  await api.delete(`mmc/delete/${vehicleId}`);
}

/**
 * Deletes multiple vehicles.
 * Uses Promise.allSettled for parallel requests.
 * Admin only.
 */
export async function deleteVehicles(
  vehicleIds: string[]
): Promise<{ succeeded: string[]; failed: string[] }> {
  const results = await Promise.allSettled(
    vehicleIds.map((id) => api.delete(`mmc/delete/${id}`))
  );

  const succeeded: string[] = [];
  const failed: string[] = [];

  results.forEach((result, index) => {
    if (result.status === 'fulfilled') {
      succeeded.push(vehicleIds[index]);
    } else {
      failed.push(vehicleIds[index]);
    }
  });

  return { succeeded, failed };
}
